import WebSocketClient from '@gamestdio/websocket';
import { isAction } from '@reduxjs/toolkit';
import { addToast } from '~/components/Toast/ToastProvider';
import { User, Event, Websockets as WebsocketsAT } from '~/redux/actionTypes';
import { fetchFollowers } from '~/redux/actions/EventActions';
import { selectCurrentUser } from '~/redux/slices/auth';
import { appConfig } from '~/utils/appConfig';
import createQueryString from '~/utils/createQueryString';
import { emitSocketEvent } from '~/utils/socket/useTransientSocketEvent';
import type { Middleware } from '@reduxjs/toolkit';
import type { AppDispatch } from '~/redux/createStore';
import type { RootState } from '~/redux/rootReducer';

const createWebSocketMiddleware = (): Middleware<
  Record<string, never>,
  RootState
> => {
  let socket: WebSocketClient | null = null;
  return ({ getState, dispatch: storeDispatch }) => {
    const dispatch = storeDispatch as AppDispatch;
    const makeSocket = (jwt: string | null) => {
      if (socket || !jwt) return;
      const qs = createQueryString({
        jwt,
      });
      socket = new WebSocketClient(`${appConfig.wsServerUrl}/${qs}`);

      socket.onmessage = (event) => {
        const { type, payload, meta: socketMeta } = JSON.parse(event.data);
        const meta = {
          ...socketMeta,
          currentUser: selectCurrentUser(getState()),
        };

        if (
          type === Event.SOCKET_REGISTRATION.SUCCESS &&
          (payload.user && payload.user.id) === meta.currentUser.id
        ) {
          dispatch(fetchFollowers(meta.eventId, meta.currentUser.id));
        }

        dispatch({
          type,
          payload,
          meta,
        });
        const message = meta.successMessage || meta.errorMessage;

        if (message) {
          addToast({
            message,
            type: meta.successMessage
              ? 'success'
              : meta.errorMessage
                ? 'error'
                : undefined,
          });
        }

        emitSocketEvent({ type, payload, meta });
      };

      socket.onopen = () => {
        dispatch({
          type: WebsocketsAT.CONNECTED,
        });
      };

      socket.onclose = () => {
        dispatch({
          type: WebsocketsAT.CLOSED,
        });
      };

      socket.onerror = () => {
        dispatch({
          type: WebsocketsAT.ERROR,
        });
      };
    };

    return (next) => (action) => {
      if (!isAction(action)) return next(action);

      if (action.type === 'REHYDRATED') {
        makeSocket(getState().auth.token);
        return next(action);
      }

      if (action.type === User.LOGIN.SUCCESS) {
        const { payload } = action as {
          type: string;
          payload: { token: string };
        };
        makeSocket(payload.token);
        return next(action);
      }

      if (action.type === User.LOGOUT) {
        if (socket) {
          socket.close();
        }

        socket = null;
        return next(action);
      }

      if (socket?.readyState === WebSocket.OPEN) {
        switch (action.type) {
          case WebsocketsAT.GROUP_JOIN.BEGIN:
          case WebsocketsAT.GROUP_LEAVE.BEGIN: {
            const { payload } = action as {
              type: string;
              payload: { group: string };
            };
            socket.send(
              JSON.stringify({
                type: action.type,
                payload,
              }),
            );
          }
        }
        return next(action);
      }

      return next(action);
    };
  };
};

export default createWebSocketMiddleware;
